import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PhoneIncoming, Upload, FileDown, RefreshCw, Loader2, ChevronDown, Undo2, Eraser, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { downloadExport } from "@/lib/downloadExport";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/** Genesys Call Log Import — manual upload bridge until live call events flow.
 *  Rows match leads on Customer ID; attempts land in each lead's call history. */
export default function CallLogImportPanel() {
  const { activeSlug } = useClient();
  const { hasPerm } = useAuth();
  const canUpload = hasPerm("leads.import_call_logs");
  const canRollback = hasPerm("leads.rollback_call_log_import");
  const fileRef = useRef(null);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [backfill, setBackfill] = useState(false);
  const [cleanup, setCleanup] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [integrity, setIntegrity] = useState(null);
  const [integrityBusy, setIntegrityBusy] = useState(false);
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/leads/call-logs/imports")
      .then((r) => setImports(r.data.imports || []))
      .catch(() => toast.error("Failed to load call-log import history"))
      .finally(() => setLoading(false));
    api.get("/leads/call-logs/dialled-not-dispositioned")
      .then((r) => setPending(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); setLastResult(null); }, [load, activeSlug]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("backfill_assignees", backfill ? "true" : "false");
      const { data } = await api.post("/leads/call-logs/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
      const s = data.summary;
      toast.success(`Imported ${s.entries_created} call attempt${s.entries_created === 1 ? "" : "s"} · ${s.matched} lead${s.matched === 1 ? "" : "s"} matched${s.auto_assigned ? ` · ${s.auto_assigned} backfilled` : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Call log upload failed");
    } finally {
      setUploading(false);
    }
  };

  const runCleanupPreview = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.get("/leads/call-logs/cleanup-preview");
      setCleanup(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup preview failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const runCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post("/leads/call-logs/cleanup-assignments");
      toast.success(`Cleared ${data.cleared} import-only assignment(s) — now re-deriving ownership from disposition history (matrix v${data.matrix_version}). Check Settings → Assigning Logic for the result.`);
      setCleanup(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  const runIntegrityPreview = async () => {
    setIntegrityBusy(true);
    try {
      const { data } = await api.get("/leads/call-logs/integrity-report");
      setIntegrity(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Integrity check failed");
    } finally {
      setIntegrityBusy(false);
    }
  };

  const runIntegrityCleanup = async () => {
    setIntegrityBusy(true);
    try {
      const { data } = await api.post("/leads/call-logs/cleanup-invalid-entries");
      toast.success(`Removed ${data.entries_removed} invalid call entr${data.entries_removed === 1 ? "y" : "ies"} from ${data.leads_touched} lead(s) — Total Dials restored.`);
      setIntegrity(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setIntegrityBusy(false);
    }
  };

  const downloadErrors = async (imp) => {
    try {
      const res = await api.get(`/leads/call-logs/imports/${imp.id}/errors`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `call-log-errors-${imp.id.slice(0, 8)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No error report available for this import");
    }
  };

  const exportData = async (path, name, format) => {
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadExport(path, { format }, `${name}-${activeSlug}-${stamp}.${format}`);
      toast.success(`Export downloaded (.${format})`);
    } catch {
      toast.error("Export failed");
    }
  };

  const s = lastResult?.summary;

  const rollback = async (imp) => {
    if (!window.confirm(`Roll back "${imp.file_name}"? This removes its call history entries and reverts assignments it made.`)) return;
    setRollingBack(imp.id);
    try {
      const { data } = await api.post(`/leads/call-logs/imports/${imp.id}/rollback`);
      toast.success(`Rolled back — ${data.entries_removed} attempts removed, ${data.assignments_reverted} assignments reverted (${data.leads_touched} leads)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(null);
    }
  };

  return (
    <Card data-testid="call-log-import-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <PhoneIncoming className="w-4 h-4 text-slate-500" /> Genesys Call Log Import
        </CardTitle>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-call-data-btn">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> Export
                <ChevronDown className="w-3 h-3 ml-1 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportData("/leads/call-logs/export", "kippu-call-activity", "csv")} data-testid="export-call-activity-csv">
                Call Activity — CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData("/leads/call-logs/export", "kippu-call-activity", "xlsx")} data-testid="export-call-activity-xlsx">
                Call Activity — Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData("/leads/imports/export", "kippu-import-history", "csv")} data-testid="export-import-history-csv">
                Import History — CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData("/leads/imports/export", "kippu-import-history", "xlsx")} data-testid="export-import-history-xlsx">
                Import History — Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-call-imports-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {canUpload && (
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="bg-indigo-600 hover:bg-indigo-700" data-testid="upload-call-log-btn">
              {uploading
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              {uploading ? "Importing…" : "Upload Genesys Export"}
            </Button>
          )}
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden"
                 onChange={onFile} data-testid="call-log-file-input" />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-slate-500 leading-snug">
          Upload the native Genesys contact-list export (.xlsx / .csv). Rows are matched to leads by
          <strong> Customer ID</strong>; every <span className="font-mono text-[10px]">CallRecordLastAttempt-Phone1/2/3</span> becomes
          a call-history entry (timestamp, number, agent, result, wrap-up), with Genesys results mapped to Kippu dispositions.
          Re-uploads are de-duplicated on Customer ID + phone + attempt timestamp.
          <strong> The import never sets the assignee</strong> — ownership comes only from a disposition saved in Kippu.
        </p>

        {canUpload && (
          <label className="flex items-start gap-2 text-[11px] text-slate-600 bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer">
            <Checkbox checked={backfill} onCheckedChange={(v) => setBackfill(!!v)}
                      data-testid="backfill-assignees-toggle" className="mt-0.5" />
            <span>
              <strong>Backfill assignees from this file</strong> (off by default). Assigns ONLY leads
              that are unassigned, carry an Agent Name and have no Kippu disposition. Logged as
              <span className="font-mono text-[10px]"> import_backfill</span>. Never use this on a daily snapshot.
            </span>
          </label>
        )}

        {canRollback && (
          <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded p-2"
               data-testid="cleanup-card">
            <span className="text-[11px] text-slate-600 flex-1 min-w-[220px]">
              <strong>Clean up import-only assignments</strong> — clears every assignee that can only be
              traced to a call-log import (PRD v2.0 §2.3). Disposition, manual and language assignments are untouched.
            </span>
            <Button variant="outline" size="sm" className="h-7 text-[10px]" disabled={cleanupBusy}
                    onClick={runCleanupPreview} data-testid="cleanup-preview-btn">
              {cleanupBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eraser className="w-3 h-3 mr-1" />}
              Preview cleanup
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-[10px]"
                    onClick={() => exportData("/leads/call-logs/cleanup-preview", "import-assignment-cleanup", "csv")}
                    data-testid="cleanup-csv-btn">
              <FileDown className="w-3 h-3 mr-1" /> Cleanup list CSV
            </Button>
          </div>
        )}

        {cleanup && (
          <div className="border border-rose-200 bg-rose-50 rounded p-2 space-y-2" data-testid="cleanup-preview">
            <div className="text-[11px] text-rose-800">
              <strong data-testid="cleanup-count">{cleanup.count}</strong> lead(s) would lose their assignee.
            </div>
            {(cleanup.leads || []).slice(0, 5).map((l) => (
              <div key={l.id} className="text-[10px] font-mono text-rose-700">
                {l.customer_id} · {l.customer_name} → clears {l.assigned_to} ({l.source})
              </div>
            ))}
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-[10px] bg-rose-600 hover:bg-rose-700"
                      disabled={cleanupBusy || !cleanup.count}
                      onClick={runCleanup} data-testid="cleanup-run-btn">
                Clear {cleanup.count} assignment(s)
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-[10px]"
                      onClick={() => setCleanup(null)} data-testid="cleanup-dismiss-btn">
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {pending && (
          <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded p-2"
               data-testid="integrity-card">
            <span className="text-[11px] text-slate-600 flex-1 min-w-[220px]">
              <strong>Call history integrity</strong> — finds imported call entries that cannot belong to
              their lead: blank phone slots (Genesys dialled an empty column) or a number that is not one of
              the lead&apos;s contact numbers. Removing them restores Total Dials.
            </span>
            <Button variant="outline" size="sm" className="h-7 text-[10px]" disabled={integrityBusy}
                    onClick={runIntegrityPreview} data-testid="integrity-preview-btn">
              {integrityBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
              Check integrity
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-[10px]"
                    onClick={() => exportData("/leads/call-logs/integrity-report", "call-history-integrity", "csv")}
                    data-testid="integrity-csv-btn">
              <FileDown className="w-3 h-3 mr-1" /> Report CSV
            </Button>
          </div>
        )}

        {integrity && (
          <div className={`border rounded p-2 space-y-2 ${integrity.entries_invalid ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}
               data-testid="integrity-preview">
            <div className={`text-[11px] ${integrity.entries_invalid ? "text-rose-800" : "text-emerald-800"}`}>
              <strong data-testid="integrity-entries">{integrity.entries_invalid}</strong> invalid call entr{integrity.entries_invalid === 1 ? "y" : "ies"} on{" "}
              <strong data-testid="integrity-leads">{integrity.leads_affected}</strong> lead(s)
              {integrity.by_issue?.blank_phone ? ` · blank phone: ${integrity.by_issue.blank_phone}` : ""}
              {integrity.by_issue?.phone_not_on_lead ? ` · phone not on lead: ${integrity.by_issue.phone_not_on_lead}` : ""}
            </div>
            {Object.entries(integrity.by_result || {}).slice(0, 5).map(([res, n]) => (
              <div key={res} className="text-[10px] font-mono text-rose-700">{res}: {n}</div>
            ))}
            {(integrity.leads || []).slice(0, 5).map((l) => (
              <div key={l.id} className="text-[10px] font-mono text-rose-700" data-testid={`integrity-lead-${l.customer_id}`}>
                {l.customer_id} · {l.customer_name} → {l.invalid} of {l.attempts} dial(s) invalid ({l.issues.join(", ")})
              </div>
            ))}
            <div className="flex gap-2">
              {canRollback && (
                <Button size="sm" className="h-7 text-[10px] bg-rose-600 hover:bg-rose-700"
                        disabled={integrityBusy || !integrity.entries_invalid}
                        onClick={runIntegrityCleanup} data-testid="integrity-run-btn">
                  Remove {integrity.entries_invalid} entr{integrity.entries_invalid === 1 ? "y" : "ies"}
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 text-[10px]"
                      onClick={() => setIntegrity(null)} data-testid="integrity-dismiss-btn">
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {pending && (
          <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded p-2"
               data-testid="dialled-not-dispositioned">
            <span className="text-[11px] text-slate-600 flex-1 min-w-[220px]">
              <strong>Dialled, not dispositioned</strong> — leads with calls but no disposition saved in
              Kippu. Unassigned by design; they need a disposition to gain an owner.
            </span>
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]"
                   data-testid="dialled-not-dispositioned-count">
              {pending.total ?? 0} lead(s)
            </Badge>
            <Button variant="outline" size="sm" className="h-7 text-[10px]"
                    onClick={() => exportData("/leads/call-logs/dialled-not-dispositioned", "dialled-not-dispositioned", "csv")}
                    data-testid="dialled-not-dispositioned-csv">
              <FileDown className="w-3 h-3 mr-1" /> Download CSV
            </Button>
          </div>
        )}

        {s && (
          <div className="flex flex-wrap gap-2" data-testid="call-log-import-summary">
            <Badge variant="outline" className="text-[10px]">Rows: {s.rows_total}</Badge>
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Matched: {s.matched}</Badge>
            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">Attempts imported: {s.entries_created}</Badge>
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Duplicates skipped: {s.duplicates}</Badge>
            <Badge className={`text-[10px] ${s.skipped > 0 ? "bg-rose-100 text-rose-700 hover:bg-rose-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}`}>
              Unmatched rows: {s.skipped}
            </Badge>
            {s.name_mismatches > 0 && (
              <Badge variant="outline" className="text-[10px] text-slate-500">Name mismatches: {s.name_mismatches}</Badge>
            )}
            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]" data-testid="summary-auto-assigned">
              Assigned (backfill): {s.auto_assigned ?? 0}
            </Badge>
            {(s.agents_unmatched ?? 0) > 0 && (
              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]" data-testid="summary-agents-unmatched">
                Unknown agents: {s.agents_unmatched}
              </Badge>
            )}
            {(s.blank_phone_slots ?? 0) > 0 && (
              <Badge variant="outline" className="text-[10px] text-slate-500" data-testid="summary-blank-phone-slots">
                Blank phone slots ignored: {s.blank_phone_slots}
              </Badge>
            )}
            {(s.phone_not_on_lead ?? 0) > 0 && (
              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]" data-testid="summary-phone-not-on-lead">
                Phone not on lead: {s.phone_not_on_lead}
              </Badge>
            )}
          </div>
        )}

        {imports.length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center">No call-log imports yet — upload your first Genesys export.</div>
        ) : (
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-3 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">File</th>
                  <th className="text-left px-2 py-1.5">Uploaded by</th>
                  <th className="text-right px-2 py-1.5">Rows</th>
                  <th className="text-right px-2 py-1.5">Matched</th>
                  <th className="text-right px-2 py-1.5">Attempts</th>
                  <th className="text-right px-2 py-1.5">Dupes</th>
                  <th className="text-right px-2 py-1.5">Skipped</th>
                  <th className="text-right px-2 py-1.5">Assigned</th>
                  <th className="text-right px-2 py-1.5">Agent unmatched</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {imports.map((imp) => {
                  const sm = imp.summary || {};
                  return (
                    <tr key={imp.id} className="border-t border-slate-100" data-testid={`call-import-row-${imp.id}`}>
                      <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">
                        {(imp.created_at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-2 py-1.5 text-slate-700">
                        {imp.file_name}
                        {imp.rolled_back && (
                          <Badge variant="outline" className="ml-1.5 text-[9px] text-slate-400 border-slate-300">
                            rolled back
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">{imp.uploaded_by_name || imp.uploaded_by_email}</td>
                      <td className="px-2 py-1.5 text-right">{sm.rows_total ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">{sm.matched ?? 0}</Badge>
                      </td>
                      <td className="px-2 py-1.5 text-right">{sm.entries_created ?? 0}</td>
                      <td className="px-2 py-1.5 text-right text-slate-500">{sm.duplicates ?? 0}</td>
                      <td className="px-2 py-1.5 text-right">
                        {sm.skipped > 0
                          ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">{sm.skipped}</Badge>
                          : <span className="text-slate-400">0</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right" data-testid={`call-import-assigned-${imp.id}`}>
                        {sm.auto_assigned ? (
                          <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px]">
                            {sm.auto_assigned}
                          </Badge>
                        ) : <span className="text-slate-400">0</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right text-slate-500">{sm.agents_unmatched ?? 0}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {imp.has_error_report && (
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                                  onClick={() => downloadErrors(imp)} data-testid={`call-import-errors-${imp.id}`}>
                            <FileDown className="w-3 h-3 mr-1" /> Errors
                          </Button>
                        )}
                        {canRollback && !imp.rolled_back && (
                          <Button variant="ghost" size="sm"
                                  className="h-6 px-2 text-[10px] text-rose-600 hover:text-rose-700"
                                  disabled={rollingBack === imp.id}
                                  onClick={() => rollback(imp)}
                                  data-testid={`call-import-rollback-${imp.id}`}>
                            <Undo2 className="w-3 h-3 mr-1" />
                            {rollingBack === imp.id ? "Rolling back…" : "Roll back"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
